import type { ProfileCommon } from "@/core/types";

import { PERMISSION_LIST } from "../constants/permission-list";
import type { PermissionKey } from "../types";

/**
 * Pure function to check if a user has a specific permission.
 *
 * First checks role-based exclusions, then evaluates optional allowIf predicate
 * which can grant access based on user profile data (email, insurance, age, etc).
 *
 * @param role - User's role from Redux state (string, null, or undefined)
 * @param profile - User's profile data (optional)
 * @param permissionKey - Permission identifier from PERMISSION_LIST
 * @returns true if user has permission, false otherwise
 */
type CheckPermissionByRoleProps = {
  permissionKey: PermissionKey;
  profile?: ProfileCommon;
  role: null | string | undefined;
};

export function checkPermissionByRole({
  permissionKey,
  profile,
  role,
}: CheckPermissionByRoleProps): boolean {
  if (!role) return false;

  const permission = PERMISSION_LIST[permissionKey];

  if (!permission) return false;

  const isRoleAllowed = !permission.excludedRoles.includes(role);

  // If role is allowed, grant access
  if (isRoleAllowed) return true;

  // Role is excluded - check if allowIf predicate grants access
  if (permission.allowIf) {
    return permission.allowIf({ profile, role });
  }

  return false;
}
